import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import './PortalLoginCustom.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PortalLoginCustom = () => {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const checkSession = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/custom-auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          navigate('/portal/dashboard');
        }
      } catch (err) {
        // Not logged in, stay on login page
      }
    };
    checkSession();
  }, [navigate]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      // Verify email has correct domain
      if (!email.endsWith('@agwesternrail.com')) {
        throw new Error('Only @agwesternrail.com emails can reset passwords');
      }

      const response = await fetch(`${BACKEND_URL}/api/custom-auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, new_password: newPassword })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Password reset failed');
      }

      setSuccessMessage('✅ Password reset successful! You can now sign in with your new password.');
      
      // Clear form and switch to login after 2 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setNewPassword('');
        setSuccessMessage('');
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const endpoint = isRegister ? '/api/custom-auth/register' : '/api/custom-auth/login';
      const body = isRegister 
        ? { email, password, name }
        : { email, password };

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Authentication failed');
      }

      const userData = await response.json();
      
      // Store user data in localStorage
      localStorage.setItem('portal_session', JSON.stringify(userData));
      localStorage.setItem('portal_user', JSON.stringify(userData));

      // Redirect to dashboard
      navigate('/portal/dashboard');

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="portal-login-custom-page" style={{
      backgroundImage: 'url(https://customer-assets.emergentagent.com/job_western-rail-hub/artifacts/lynqfdxe_ChatGPT%20Image%20May%2024%2C%202026%2C%2011_56_20%20PM.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Dark overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1
      }}></div>
      
      <div className="portal-login-custom-container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="portal-login-custom-card">
          <div className="portal-login-custom-header">
            <h1>AG&WESTERN Rail</h1>
            <h2>Employee Portal</h2>
            <p>
              {showForgotPassword 
                ? 'Reset your password' 
                : isRegister 
                ? 'Create your account' 
                : 'Sign in to your account'}
            </p>
          </div>

          {showForgotPassword ? (
            <form onSubmit={handleForgotPassword} className="portal-login-custom-form">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="yourname@agwesternrail.com"
                  required
                />
                <small>Only @agwesternrail.com emails allowed</small>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    minLength="8"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#666',
                      padding: '5px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <small>Minimum 8 characters</small>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="success-message">
                  {successMessage}
                </div>
              )}

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>

              <div className="portal-login-custom-toggle">
                <p>
                  Remember your password?{' '}
                  <button type="button" onClick={() => {
                    setShowForgotPassword(false);
                    setError('');
                    setSuccessMessage('');
                    setNewPassword('');
                  }}>
                    Back to Sign In
                  </button>
                </p>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="portal-login-custom-form">
            {isRegister && (
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="yourname@agwesternrail.com"
                required
              />
              <small>Only @agwesternrail.com emails allowed</small>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {!isRegister && (
                <button 
                  type="button"
                  className="forgot-password-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot Password?
                </button>
              )}
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                'Please wait...'
              ) : (
                <>
                  {isRegister ? <UserPlus size={18} /> : <LogIn size={18} />}
                  {isRegister ? 'Create Account' : 'Sign In'}
                </>
              )}
            </button>
          </form>
          )}

          {!showForgotPassword && (
          <div className="portal-login-custom-toggle">
            {isRegister ? (
              <p>
                Already have an account?{' '}
                <button onClick={() => {
                  setIsRegister(false);
                  setError('');
                }}>
                  Sign In
                </button>
              </p>
            ) : (
              <p>
                First time here?{' '}
                <button onClick={() => {
                  setIsRegister(true);
                  setError('');
                }}>
                  Create Account
                </button>
              </p>
            )}
          </div>
          )}

          <div className="portal-login-custom-footer">
            <p>For AG&WESTERN Rail employees only</p>
            <p>Need help? Contact <a href="mailto:support@agwesternrail.com">support@agwesternrail.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalLoginCustom;
